/**
 * App-wide keyboard shortcut layer.
 *
 * Built on `@tanstack/react-hotkeys` (which drives the singleton HotkeyManager)
 * and wired so the whole product is keyboard-reachable:
 *
 * - `Mod+K` opens the command palette.
 * - `` ` `` opens the add-widget picker (select + configure a Postgres widget).
 * - `Mod+T` / `Mod+W` open / close the active terminal tab.
 * - `Mod+1…9` activate the Nth terminal tab.
 * - `Option/Alt` mirrors the terminal-tab chords for the browser, where the
 *   `Cmd`/`Ctrl` digits, `+T`, and `+W` are reserved by the browser chrome and
 *   can never reach the page: `Alt+T` / `Alt+W` / `Alt+1…9`.
 * - `Alt+Shift+1/2/3` jump between the main surfaces (Home/Box/Apps).
 * - `g b` / `g a` are Vim-style sequences to Box and Apps.
 *
 * `Mod` is Cmd on macOS and Ctrl elsewhere (the desktop/mobile shells take the
 * `Mod` chords; the browser falls back to `Alt`). Themes own no global shortcut:
 * switching is exposed exclusively through the command palette's "Theme…"
 * command.
 *
 * `HOTKEYS` is the single source of truth for every shortcut — the command
 * palette's "Shortcuts" view renders the live registrations (with the `meta`
 * attached here) via `useHotkeyRegistrations`, so what you register is exactly
 * what users see.
 */

import type { ReactNode } from "react";
import {
  HotkeysProvider,
  formatForDisplay,
  useHotkeys,
  useHotkeySequence,
} from "@tanstack/react-hotkeys";

/** Highest tab index reachable by the digit tab-switch chords. */
export const TAB_SWITCH_HOTKEY_COUNT = 9;

/** Central registry. Every shortcut in the app is declared here. */
export const HOTKEYS = {
  /** Open the command palette. */
  openCommandMenu: "Mod+K",
  /** Open the add-widget picker (select + configure a widget). */
  addWidget: { key: "`" },
  /** Jump between surfaces. `Mod+1/2/3` are browser-reserved, so use a chord. */
  goToHome: "Alt+Shift+1",
  goToBox: "Alt+Shift+2",
  goToApps: "Alt+Shift+3",
  /** Open a new workspace tab (the command-palette create flow). */
  newTab: "Mod+T",
  /** Browser-safe mirror of {@link HOTKEYS.newTab}. */
  newTabAlt: "Alt+T",
  /** Close the active workspace tab. */
  closeTab: "Mod+W",
  /** Browser-safe mirror of {@link HOTKEYS.closeTab}. */
  closeTabAlt: "Alt+W",
  /** Undo the last canvas edit in the active workspace tab. */
  undo: "Mod+Z",
  /** Redo the last undone canvas edit. */
  redo: "Mod+Shift+Z",
  sequenceGoBox: ["G", "B"],
  sequenceGoApps: ["G", "A"],
} as const;

/** `Mod+N` hotkey for the Nth terminal tab (1-based). */
export type TabSwitchHotkey =
  | "Mod+1"
  | "Mod+2"
  | "Mod+3"
  | "Mod+4"
  | "Mod+5"
  | "Mod+6"
  | "Mod+7"
  | "Mod+8"
  | "Mod+9";

/** `Alt+1…9` browser-safe mirror of {@link TabSwitchHotkey}. */
export type TabSwitchFallbackHotkey =
  | "Alt+1"
  | "Alt+2"
  | "Alt+3"
  | "Alt+4"
  | "Alt+5"
  | "Alt+6"
  | "Alt+7"
  | "Alt+8"
  | "Alt+9";

export function tabSwitchHotkey(index: number): TabSwitchHotkey {
  return `Mod+${index + 1}` as TabSwitchHotkey;
}

export function tabSwitchFallbackHotkey(
  index: number,
): TabSwitchFallbackHotkey {
  return `Alt+${index + 1}` as TabSwitchFallbackHotkey;
}

/** Format a registered hotkey for display on the current platform. */
export function formatHotkey(hotkey: string): string {
  return formatForDisplay(hotkey);
}

/** Defaults applied to every hotkey in the app. */
export function AppHotkeysProvider({ children }: { children: ReactNode }) {
  return (
    <HotkeysProvider
      defaultOptions={{
        hotkey: { preventDefault: true, stopPropagation: false },
        hotkeySequence: { timeout: 1500 },
      }}
    >
      {children}
    </HotkeysProvider>
  );
}

/** Actions the app-level hotkeys drive. */
export interface AppHotkeyActions {
  openCommandMenu: () => void;
  /** Open the add-widget picker for the active workspace. */
  addWidget: () => void;
  goToHome: () => void;
  goToBox: () => void;
  goToApps: () => void;
  /**
   * Open a new workspace tab. Optional so the hotkey layer stays usable while
   * the terminal tab store is being wired by the page-planner workstream; when
   * the action is absent the shortcut is simply not registered.
   */
  newTab?: () => void;
  /** Close the active workspace tab. Same optionality contract as `newTab`. */
  closeTab?: () => void;
  /** Activate the Nth terminal tab (0-based). Same contract as `newTab`. */
  selectTab?: (index: number) => void;
}

/**
 * Register the global app hotkeys. Mount once near the root. Callbacks are
 * re-synced on every render, so actions may safely close over current state.
 */
export function useAppHotkeys(actions: AppHotkeyActions) {
  useHotkeys(
    [
      {
        hotkey: HOTKEYS.openCommandMenu,
        callback: actions.openCommandMenu,
        options: {
          meta: {
            name: "Open command palette",
            description: "Jump to any command, page, or security",
          },
        },
      },
      {
        hotkey: HOTKEYS.addWidget,
        callback: actions.addWidget,
        options: {
          meta: {
            name: "Add widget",
            description: "Select and configure a widget for this workspace",
          },
        },
      },
      {
        hotkey: HOTKEYS.goToHome,
        callback: actions.goToHome,
        options: {
          meta: { name: "Go to home", description: "Research terminal" },
        },
      },
      {
        hotkey: HOTKEYS.goToBox,
        callback: actions.goToBox,
        options: {
          meta: { name: "Go to Box", description: "Ito mailbox" },
        },
      },
      {
        hotkey: HOTKEYS.goToApps,
        callback: actions.goToApps,
        options: {
          meta: {
            name: "Go to Apps",
            description: "Ito compute",
          },
        },
      },
      // Terminal-tab shortcuts. Registered only when the host wires the
      // action, so `hotkeys.tsx` stays the source of truth without forcing a
      // dependency on the (parallel) terminal tab store.
      ...(actions.newTab
        ? [
            {
              hotkey: HOTKEYS.newTab,
              callback: actions.newTab,
              options: {
                meta: {
                  name: "New tab",
                  description: "Create a new workspace tab",
                },
              },
            },
            {
              hotkey: HOTKEYS.newTabAlt,
              callback: actions.newTab,
              options: {
                meta: {
                  name: "New tab (browser)",
                  description: "Alt/Option mirror of ⌘T",
                },
              },
            },
          ]
        : []),
      ...(actions.closeTab
        ? [
            {
              hotkey: HOTKEYS.closeTab,
              callback: actions.closeTab,
              options: {
                meta: {
                  name: "Close tab",
                  description: "Close the active workspace tab",
                },
              },
            },
            {
              hotkey: HOTKEYS.closeTabAlt,
              callback: actions.closeTab,
              options: {
                meta: {
                  name: "Close tab (browser)",
                  description: "Alt/Option mirror of ⌘W",
                },
              },
            },
          ]
        : []),
      ...(actions.selectTab
        ? Array.from({ length: TAB_SWITCH_HOTKEY_COUNT }, (_, index) => [
            {
              hotkey: tabSwitchHotkey(index),
              callback: () => actions.selectTab?.(index),
              options: {
                meta: {
                  name: `Switch to tab ${index + 1}`,
                  description: "Activate the Nth open workspace tab",
                },
              },
            },
            {
              hotkey: tabSwitchFallbackHotkey(index),
              callback: () => actions.selectTab?.(index),
              options: {
                meta: {
                  name: `Switch to tab ${index + 1} (browser)`,
                  description: "Alt/Option mirror of the ⌘ digit chord",
                },
              },
            },
          ]).flat()
        : []),
    ],
    { preventDefault: true },
  );

  useHotkeySequence([...HOTKEYS.sequenceGoBox], actions.goToBox, {
    meta: { name: "Go to Box", description: "Sequence: g then b" },
  });
  useHotkeySequence([...HOTKEYS.sequenceGoApps], actions.goToApps, {
    meta: { name: "Go to Apps", description: "Sequence: g then a" },
  });
}
